// מפת "משתנים לחיצים" — השדות שכל אירוע נושא ב-payload שלו (data.*), לפי
// מבנה ה-Events::emit בפועל ב-backend. משותף למסך ה-Webhooks ולבונה
// האוטומציות: לחיצה על שבב מזריקה את הטוקן {{data.שדה}} לשדה הפעיל.

export type Var = { token: string; label: string };

// משתנים כלליים — זמינים בכל אירוע.
export const GENERAL_VARS: Var[] = [
  { token: '{{event}}', label: 'סוג האירוע' },
  { token: '{{tenant_id}}', label: 'מזהה מרכזיה' },
  { token: '{{timestamp}}', label: 'חותמת זמן' },
];

// שדות "עדכון CRM ישיר" — מצורפים לכל אירועי השיחה (ended/missed/wrapup/
// transcript): פרטי נציג ולקוח בפורמטים מוכנים, מזהים, משך MM:SS, הקלטה וסיכום.
const CALL_CRM: Var[] = [
  { token: '{{data.record_id}}', label: 'Record ID (מזהה שיחה)' },
  { token: '{{data.session_id}}', label: 'Session ID' },
  { token: '{{data.agent_email}}', label: 'אימייל נציג' },
  { token: '{{data.customer_id}}', label: 'מזהה לקוח (972...)' },
  { token: '{{data.customer_display_phone}}', label: 'טלפון לקוח לתצוגה (05X-XXX-XXXX)' },
  { token: '{{data.customer_name}}', label: 'שם לקוח (מה-CRM)' },
  { token: '{{data.external_id}}', label: 'מזהה חיצוני של הלקוח' },
  { token: '{{data.time_in_call}}', label: 'זמן בשיחה (MM:SS)' },
  { token: '{{data.recording_link}}', label: 'קישור הקלטה' },
  { token: '{{data.ai_summary}}', label: 'סיכום AI' },
];

// אירועי מחזור-חיי שיחה (started/answered/ended/missed) — payload זהה.
const CALL_LIFECYCLE: Var[] = [
  { token: '{{data.call_id}}', label: 'מזהה שיחה' },
  { token: '{{data.direction}}', label: 'כיוון (נכנס/יוצא)' },
  { token: '{{data.status}}', label: 'סטטוס שיחה' },
  { token: '{{data.from_number}}', label: 'מספר מתקשר' },
  { token: '{{data.to_number}}', label: 'מספר יעד' },
  { token: '{{data.started_at}}', label: 'מתי התחילה' },
  { token: '{{data.answered_at}}', label: 'מתי נענתה' },
  { token: '{{data.ended_at}}', label: 'מתי הסתיימה' },
  { token: '{{data.duration_sec}}', label: 'משך (שניות)' },
  { token: '{{data.agent_id}}', label: 'מזהה נציג' },
  { token: '{{data.agent_name}}', label: 'שם נציג' },
  { token: '{{data.queue_id}}', label: 'מזהה תור' },
  { token: '{{data.queue_name}}', label: 'שם תור' },
  { token: '{{data.campaign_id}}', label: 'מזהה קמפיין' },
  { token: '{{data.campaign_name}}', label: 'שם קמפיין' },
  { token: '{{data.recording_url}}', label: 'קישור להקלטה' },
  ...CALL_CRM,
];
const LEAD_DIALER: Var[] = [
  { token: '{{data.lead_id}}', label: 'מזהה ליד' },
  { token: '{{data.external_id}}', label: 'מזהה חיצוני (CRM)' },
  { token: '{{data.attempts}}', label: 'מספר נסיונות חיוג' },
  { token: '{{data.phone}}', label: 'טלפון הלקוח' },
  { token: '{{data.name}}', label: 'שם הלקוח' },
  { token: '{{data.new_status}}', label: 'סטטוס חדש' },
  { token: '{{data.old_status}}', label: 'סטטוס קודם' },
  { token: '{{data.disposition}}', label: 'קוד סיום' },
  { token: '{{data.campaign_id}}', label: 'מזהה קמפיין' },
  { token: '{{data.campaign_name}}', label: 'שם קמפיין' },
  { token: '{{data.agent_id}}', label: 'מזהה נציג' },
  { token: '{{data.agent_name}}', label: 'שם נציג' },
  { token: '{{data.callback_at}}', label: 'מועד שיחה חוזרת' },
  { token: '{{data.fields.שדה}}', label: 'שדה מותאם של הליד' },
];
const LEAD_CRM: Var[] = [
  { token: '{{data.id}}', label: 'מזהה ליד' },
  { token: '{{data.external_id}}', label: 'מזהה חיצוני' },
  { token: '{{data.phone}}', label: 'טלפון' },
  { token: '{{data.name}}', label: 'שם' },
  { token: '{{data.email}}', label: 'אימייל' },
  { token: '{{data.company}}', label: 'חברה' },
  { token: '{{data.status}}', label: 'סטטוס' },
  { token: '{{data.source}}', label: 'מקור' },
  { token: '{{data.tags}}', label: 'תגיות' },
  { token: '{{data.fields.שדה}}', label: 'שדה מותאם' },
];

export const EVENT_VARS: Record<string, Var[]> = {
  'call.started': CALL_LIFECYCLE,
  'call.answered': CALL_LIFECYCLE,
  'call.ended': CALL_LIFECYCLE,
  'call.missed': CALL_LIFECYCLE,
  'call.recording.ready': [
    { token: '{{data.call_id}}', label: 'מזהה שיחה' },
    { token: '{{data.direction}}', label: 'כיוון' },
    { token: '{{data.from_number}}', label: 'מספר מתקשר' },
    { token: '{{data.to_number}}', label: 'מספר יעד' },
    { token: '{{data.talk_sec}}', label: 'זמן דיבור (שניות)' },
    { token: '{{data.recording_url}}', label: 'קישור להקלטה' },
  ],
  'call.transcript.ready': [
    { token: '{{data.call_id}}', label: 'מזהה שיחה' },
    { token: '{{data.direction}}', label: 'כיוון' },
    { token: '{{data.from_number}}', label: 'מספר מתקשר' },
    { token: '{{data.to_number}}', label: 'מספר יעד' },
    { token: '{{data.agent_id}}', label: 'מזהה נציג' },
    { token: '{{data.summary}}', label: 'סיכום AI' },
    ...CALL_CRM,
    { token: '{{data.sentiment}}', label: 'סנטימנט' },
    { token: '{{data.highlights}}', label: 'נקודות עיקריות' },
    { token: '{{data.ai_metrics.score}}', label: 'ציון AI' },
  ],
  'call.wrapup.saved': [
    { token: '{{data.call_id}}', label: 'מזהה שיחה' },
    { token: '{{data.direction}}', label: 'כיוון' },
    { token: '{{data.phone}}', label: 'טלפון הלקוח' },
    { token: '{{data.agent_id}}', label: 'מזהה נציג' },
    { token: '{{data.agent_name}}', label: 'שם נציג' },
    { token: '{{data.disposition}}', label: 'קוד סיום' },
    { token: '{{data.notes}}', label: 'תיעוד הנציג' },
    { token: '{{data.tags}}', label: 'תגיות' },
    { token: '{{data.callback_at}}', label: 'מועד שיחה חוזרת' },
    ...CALL_CRM,
  ],
  'callback.created': [
    { token: '{{data.callback_id}}', label: 'מזהה שיחה חוזרת' },
    { token: '{{data.phone}}', label: 'טלפון' },
    { token: '{{data.name}}', label: 'שם' },
    { token: '{{data.scheduled_at}}', label: 'מתי מתוזמן' },
    { token: '{{data.agent_id}}', label: 'מזהה נציג' },
  ],
  'callback.due': [
    { token: '{{data.callback_id}}', label: 'מזהה שיחה חוזרת' },
    { token: '{{data.phone}}', label: 'טלפון' },
    { token: '{{data.name}}', label: 'שם' },
    { token: '{{data.scheduled_at}}', label: 'מתי מתוזמן' },
    { token: '{{data.agent_id}}', label: 'מזהה נציג' },
  ],
  'voicemail.received': [
    { token: '{{data.voicemail_id}}', label: 'מזהה הודעה' },
    { token: '{{data.mailbox_ext}}', label: 'שלוחת תא קולי' },
    { token: '{{data.from_number}}', label: 'מספר מתקשר' },
    { token: '{{data.duration}}', label: 'משך (שניות)' },
    { token: '{{data.file_url}}', label: 'קישור לקובץ' },
  ],
  'ticket.created': [
    { token: '{{data.ticket_id}}', label: 'מזהה טיקט' },
    { token: '{{data.subject}}', label: 'נושא' },
    { token: '{{data.priority}}', label: 'עדיפות' },
    { token: '{{data.opened_by}}', label: 'מי פתח (מזהה)' },
  ],
  'ticket.replied': [
    { token: '{{data.ticket_id}}', label: 'מזהה טיקט' },
    { token: '{{data.subject}}', label: 'נושא' },
    { token: '{{data.replied_by}}', label: 'מי הגיב (מזהה)' },
  ],
  'contact.created': [
    { token: '{{data.contact_id}}', label: 'מזהה איש קשר' },
    { token: '{{data.phone}}', label: 'טלפון' },
    { token: '{{data.name}}', label: 'שם' },
    { token: '{{data.status}}', label: 'סטטוס' },
    { token: '{{data.owner_id}}', label: 'מזהה בעלים' },
    { token: '{{data.tags}}', label: 'תגיות' },
  ],
  'contact.updated': [
    { token: '{{data.contact_id}}', label: 'מזהה איש קשר' },
    { token: '{{data.phone}}', label: 'טלפון' },
    { token: '{{data.name}}', label: 'שם' },
    { token: '{{data.status}}', label: 'סטטוס' },
    { token: '{{data.owner_id}}', label: 'מזהה בעלים' },
    { token: '{{data.tags}}', label: 'תגיות' },
  ],
  'lead.created': LEAD_CRM,
  'lead.updated': LEAD_CRM,
  // status_changed מגיע משני מקורות (דיאלר + API) — מציגים את שני הסטים.
  'lead.status_changed': [...LEAD_DIALER, ...LEAD_CRM.filter((v) => !LEAD_DIALER.some((d) => d.token === v.token))],
  'campaign.finished': [
    { token: '{{data.campaign_id}}', label: 'מזהה קמפיין' },
    { token: '{{data.campaign_name}}', label: 'שם קמפיין' },
    { token: '{{data.total_leads}}', label: 'סה״כ לידים' },
    { token: '{{data.completed_leads}}', label: 'לידים שהושלמו' },
    { token: '{{data.finished_at}}', label: 'מתי הסתיים' },
  ],
  'automation.inbound': [
    { token: '{{data.phone}}', label: 'טלפון (אם נשלח)' },
    { token: '{{data.name}}', label: 'שם (אם נשלח)' },
    { token: '{{data.שדה}}', label: 'כל שדה שנשלח ב-payload' },
  ],
};

/** איחוד השדות של האירועים שנבחרו (ללא כפילויות), עם fallback לכל הקטלוג. */
export function varsForEvents(events: string[]): Var[] {
  if (events.length === 0) {
    const all = [...CALL_LIFECYCLE, ...LEAD_DIALER, ...LEAD_CRM];
    return dedupeVars(all);
  }
  const picked = events.flatMap((e) => EVENT_VARS[e] ?? []);
  return dedupeVars(picked);
}

export function dedupeVars(vars: Var[]): Var[] {
  const seen = new Set<string>();
  return vars.filter((v) => (seen.has(v.token) ? false : (seen.add(v.token), true)));
}
